<template>
    <div class="visitorView"
        ref="visitorViewHeight">
        <LikeButton class="likes"
            v-if="currentArtwork.flag.visibility"
                :entry="currentArtwork"
                :likedBy="social.likedBy.users"/>
        <Heading :class="[{ extraMargin: !currentArtwork.content.subtitle.en }]"
            font="secondary"
            size="xxl">
            <template v-if="title">
                {{ title }}
            </template>
            <template v-else>
                {{ siteContent.titleOfYourEntry }}
            </template>
        </Heading>
        <Heading v-if="subtitle"
            :h="2"
            size="m">
            {{ subtitle }}
        </Heading>
        <div class="contributors">
            <div class="artist">
                <Contributor class="contributorItem"
                    :slug="currentArtwork.creator.artist.slug"
                    :avatar="artistAvatar"
                    :name="currentArtwork.creator.artist.name">
                </Contributor>
                <FollowButton
                    v-if="auth.id && auth.id !== currentArtwork.creator.artist.id"
                    :followers="social.followers.users"
                    :currentUserID="currentArtwork.creator.artist.id"/>
            </div>
            <Contributor class="contributorItem"
                v-if="currentArtwork.creator.artist.id !== currentArtwork.creator.storywriter.id"
                :slug="currentArtwork.creator.storywriter.slug"
                :avatar="storywriterAvatar"
                :name="currentArtwork.creator.storywriter.name">
            </Contributor>
        </div>
        
        <div class="storyContent">
            <p class="intro">
                <template v-if="intro">
                    {{ intro }}
                </template>
            </p>
            <p class="story"
                v-if="story">
                {{ story }}
            </p>
        </div>
        
        <div class="buttonsWrapper">
            <Button class="editEntryButton"
                v-if="hasPermissionToEdit"
                @click="$emit('setEditorView', true)"
                prefixIcon="edit"
                size="small">
                {{ siteContent.edit }}
            </Button>

            <DynamicLinkModule
                route="entry"
                :id="currentEntryId"
                :title="title"
                :caption="intro"/>
        </div>
    </div>
</template>

<script>
    import { db } from '@/firebase'

    import LikeButton        from '@/components/templates/LikeButton'
    import Contributor       from '@/components/entry/Contributor'
    import FollowButton      from '@/components/templates/FollowButton'
    import DynamicLinkModule from '@/components/templates/DynamicLinkModule'

    export default {
        name: 'VisitorView',
        components: {
            LikeButton,
            Contributor,
            FollowButton,
            DynamicLinkModule
        },
        props: {
            currentArtwork: Object,
            currentEntryId: String,
        },
        data() {
            return {
                visitorViewHeight: null,
                social: {
                    followers: {
                        users: []
                    },
                    likedBy: {
                        users: []
                    }
                }
            }
        },
        computed: {
            hasPermissionToEdit() {
                if(this.currentArtwork.creator.artist.id === this.auth.id || this.permissions.isAdmin) {
                    return true
                }
            },
            title() {
                if(this.preferences.language === 'hu' && this.currentArtwork.content.title.hu) {
                    return this.currentArtwork.content.title.hu
                } else {
                    return this.currentArtwork.content.title.en
                }
            },
            subtitle() {
                if(this.preferences.language === 'hu' && this.currentArtwork.content.subtitle.hu) {
                    return this.currentArtwork.content.subtitle.hu
                } else {
                    return this.currentArtwork.content.subtitle.en
                }
            },
            intro() {
                if(this.preferences.language === 'hu' && this.currentArtwork.content.intro.hu) {
                    return this.currentArtwork.content.intro.hu
                } else {
                    return this.currentArtwork.content.intro.en
                }
            },
            story() {
                if(this.preferences.language === 'hu' && this.currentArtwork.content.story.hu) {
                    return this.currentArtwork.content.story.hu
                } else {
                    return this.currentArtwork.content.story.en
                }
            },
            artistAvatar() {
                if(this.currentArtwork.creator.artist.avatar) {
                    return this.currentArtwork.creator.artist.avatar
                } else {
                    return this.constants.defaults.avatar
                }
            },
            storywriterAvatar() {
                if(this.currentArtwork.creator.storywriter.avatar) {
                    return this.currentArtwork.creator.storywriter.avatar
                } else {
                    return this.constants.defaults.avatar
                }
            }
        },
        async mounted() {
            this.visitorViewHeight = this.$refs.visitorViewHeight.clientHeight
            this.$emit('setVisitorViewHeight', this.visitorViewHeight)

            // Fetch follower array
            try {
                await db
                    .collection('users')
                    .doc(this.currentArtwork.creator.artist.id)
                    .collection('social')
                    .doc('followers')
                    .onSnapshot((doc) => {
                        const users = doc.data()
                        this.social.followers = users

                        return () => cleanUp()
                    })
            } catch(error) {
                throw error
            }

            // Fetch likedBy array
            try {
                await db
                    .collection('entries')
                    .doc(this.currentArtwork.id)
                    .collection('social')
                    .doc('likedBy')
                    .onSnapshot((doc) => {
                        const users = doc.data()
                        this.social.likedBy = users

                        return () => cleanUp()
                    })
            } catch(error) {
                throw error
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../scss/variables';

    .visitorView {
        position: absolute;
        width: 100%;
        padding: 1.5rem 6rem 0 6rem;
    }

    .contributors {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
    }

    .artist {
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
    }

    .contributorItem {
        margin-right: 0.75rem;
    }

    .h1 {
        margin: 0;
    }

    .h2 {
        margin: 0 0 2rem 0;
        color: var(--color-grey-500);
    }

    .intro {
        font-size: 1.75rem;
        white-space: pre-line;
        margin: -1rem 0 0.5rem 0;
    }

    .story {
        font-size: 1.35rem;
        white-space: pre-line;
    }

    .buttonsWrapper {
        display: flex;
        margin-top: 2rem;
    }

    .editEntryButton {
        margin-right: 1rem;
    }

    .extraMargin {
        margin-bottom: 2rem;
    }

    .likes {
        margin-bottom: 1.5rem;
    }

    @media all and (max-width: 1440px) {
        .visitorView {
            padding: 1.5rem 2rem 0 2rem;
        }
    }

    @media all and (max-width: 1200px) {
        .visitorView {
            padding: 1rem;
        }
    }

    @media all and (max-width: 640px) {
        .visitorView {
            padding: 1rem 0.5rem;
        }
    }
</style>